// Schema responses are passed through unchanged. (Previously this backfilled a
// legacy identifier field onto each schema object; the API now only ever emits
// `schema_name`, so no normalization is needed.)
export function normalizeSchema(s) {
  return s;
}

export default {
  // Schemas
  async getSchemas(params = {}) {
    const query = new URLSearchParams(params).toString();
    const response = await this.request(`/schemas${query ? '?' + query : ''}`);
    return (response.schemas || []).map(normalizeSchema);
  },

  async getSchema(schemaId) {
    return normalizeSchema(await this.request(`/schemas/${schemaId}`));
  },

  async createSchema(data) {
    return normalizeSchema(await this.request('/schemas', {
      method: 'POST',
      body: data,
    }));
  },

  async updateSchema(schemaId, updates) {
    return normalizeSchema(await this.request(`/schemas/${schemaId}`, {
      method: 'PUT',
      body: updates,
    }));
  },

  async deleteSchema(schemaId) {
    return this.request(`/schemas/${schemaId}`, {
      method: 'DELETE',
    });
  },

  async validateSchema(schemaId, data) {
    return this.request(`/schemas/${schemaId}/validate`, {
      method: 'POST',
      body: data,
    });
  },

  async getSchemaVersions(schemaName) {
    const response = await this.request(`/schemas/name/${encodeURIComponent(schemaName)}/versions`);
    return (response.versions || []).map(normalizeSchema);
  },

  // Data updates: an app moving a person's records to another schema. One card per
  // update; the person accepts, declines, or undoes one already done.
  async getUpdates() {
    return this.request('/updates');
  },

  async acceptUpdate(updateId) {
    return this.request(`/updates/${updateId}/accept`, { method: 'POST' });
  },

  async declineUpdate(updateId) {
    return this.request(`/updates/${updateId}/decline`, { method: 'POST' });
  },

  async undoUpdate(updateId) {
    return this.request(`/updates/${updateId}/undo`, { method: 'POST' });
  },

  // Proposals
  async getProposals(params = {}) {
    const query = new URLSearchParams(params).toString();
    const response = await this.request(`/proposals${query ? '?' + query : ''}`);
    return response;
  },

  async getProposal(proposalId) {
    return this.request(`/proposals/${proposalId}`);
  },

  // No list, or an empty one, approves the whole proposal. Send names only to keep a
  // subset of its entities — `[]` once meant "none of it" and emptied every approval.
  // `seenUpdatedAt` is the version the person was shown; the server refuses (409
  // PROPOSAL_CHANGED) if the proposal changed after it.
  async approveProposal(proposalId, approvedEntities, { seenUpdatedAt } = {}) {
    return this.request(`/proposals/${proposalId}/approve`, {
      method: 'POST',
      body: {
        ...(approvedEntities?.length ? { approved_entities: approvedEntities } : {}),
        ...(seenUpdatedAt ? { seen_updated_at: seenUpdatedAt } : {}),
      },
    });
  },

  async rejectProposal(proposalId, reason = '') {
    return this.request(`/proposals/${proposalId}/reject`, {
      method: 'POST',
      body: { reason }
    });
  },

  async applyProposal(proposalId) {
    return this.request(`/proposals/${proposalId}/apply`, {
      method: 'POST'
    });
  },

  async deferProposal(proposalId) {
    return this.request(`/proposals/${proposalId}/defer`, { method: 'POST' });
  },

  async editConfirmProposal(proposalId, data) {
    return this.request(`/proposals/${proposalId}/edit-confirm`, {
      method: 'POST',
      body: data,
    });
  },

  // Questions
  async getQuestions(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/questions${query ? '?' + query : ''}`);
  },

  async getQuestion(questionId) {
    return this.request(`/questions/${questionId}`);
  },

  async answerQuestion(questionId, answerText, answerData = null) {
    return this.request(`/questions/${questionId}/answer`, {
      method: 'POST',
      body: { answer_text: answerText, answer_data: answerData },
    });
  },

  async deferQuestion(questionId) {
    return this.request(`/questions/${questionId}/defer`, { method: 'POST' });
  },

  async dismissQuestion(questionId) {
    return this.request(`/questions/${questionId}/dismiss`, { method: 'POST' });
  },
};
